import { buildTranslate } from 'locales'

import AIMessage from 'components/AI/ThanxAIMessage/AIMessage'
import LoadingMessage from 'components/AI/ThanxAIMessage/LoadingMessage'
import ToolCallMessage from 'components/AI/ThanxAIMessage/ToolCallMessage'
import UserMessage from 'components/AI/ThanxAIMessage/UserMessage'
import type { Message } from 'slices/aiConversations/types'
import './ThanxAIMessage.scss'

const t = buildTranslate('components.ai.conversation')

export interface ThanxAIMessageProps {
  message: Message
  showLabel?: boolean
  isStreaming?: boolean
  isLoading?: boolean
  isLatestDraftSegment?: boolean
  isLatestSavedSegment?: boolean
  hasAnySavedSegment?: boolean
  onCalloutAction?: (message: string) => void
  calloutActionsDisabled?: boolean
}

const getAriaLabel = (
  messageType: 'user' | 'ai' | 'tool_call',
  isStreaming: boolean
): string => {
  if (isStreaming) {
    return t('ai_typing_label')
  }

  if (messageType === 'ai') {
    return t('ai_response_label')
  }

  if (messageType === 'tool_call') {
    return t('tool_call_message')
  }

  return t('user_message_label')
}

const ThanxAIMessage = ({
  message,
  showLabel = true,
  isStreaming = false,
  isLoading = false,
  isLatestDraftSegment = false,
  isLatestSavedSegment = false,
  hasAnySavedSegment = false,
  onCalloutAction,
  calloutActionsDisabled = false,
}: ThanxAIMessageProps) => {
  if (isLoading) {
    return <LoadingMessage />
  }

  const renderMessageContent = () => {
    if (message.type === 'user') {
      return <UserMessage message={message} />
    }
    if (message.type === 'tool_call') {
      return <ToolCallMessage message={message} />
    }
    return (
      <AIMessage
        message={message}
        showLabel={showLabel}
        isStreaming={isStreaming}
        isLatestDraftSegment={isLatestDraftSegment}
        isLatestSavedSegment={isLatestSavedSegment}
        hasAnySavedSegment={hasAnySavedSegment}
        onCalloutAction={onCalloutAction}
        calloutActionsDisabled={calloutActionsDisabled}
      />
    )
  }

  return (
    <div
      className={`thanx-ai-conversation__message thanx-ai-conversation__message--${
        message.type
      } ${!showLabel ? 'thanx-ai-conversation__message--grouped' : ''}`}
      data-message-id={message.id}
      role={message.type === 'ai' ? 'log' : undefined}
      aria-label={getAriaLabel(message.type, isStreaming)}
      aria-live={isStreaming ? 'polite' : undefined}
    >
      {renderMessageContent()}
    </div>
  )
}

export default ThanxAIMessage
